
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { BasicInfoFields } from "./form/BasicInfoFields";
import { StatusFields } from "./form/StatusFields";
import { DateFields } from "./form/DateFields";
import { AssociationFields } from "./form/AssociationFields";
import { taskFormSchema, TaskFormValues } from "./types";
import { useState } from "react";
import { useOrganization } from "@/contexts/OrganizationContext";

interface TaskFormProps {
  leadId?: string;
  taskId?: string;
  initialData?: any;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export const TaskForm = ({ leadId, taskId, initialData, onSuccess, onCancel }: TaskFormProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { currentOrganization } = useOrganization();

  const form = useForm<TaskFormValues>({
    resolver: zodResolver(taskFormSchema),
    defaultValues: {
      title: initialData?.title || "",
      description: initialData?.description || "",
      priority: initialData?.priority || "medium",
      status: initialData?.status || "todo",
      progress: initialData?.progress || 0,
      due_date: initialData?.due_date ? new Date(initialData.due_date) : undefined,
      reminder_date: initialData?.reminder_date ? new Date(initialData.reminder_date) : undefined,
      lead_id: initialData?.lead_id || leadId || undefined,
      contact_id: initialData?.contact_id || undefined,
      assigned_to: initialData?.assigned_to || undefined,
    },
  });

  const onSubmit = async (values: TaskFormValues) => {
    if (!currentOrganization?.id) {
      toast({
        title: "Error",
        description: "No organization selected",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSubmitting(true);

      // Get the current user session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) throw sessionError;
      if (!session) throw new Error("No active session");

      // Format dates as ISO strings for Supabase
      const taskData = {
        title: values.title,
        description: values.description,
        priority: values.priority,
        status: values.status,
        progress: values.progress,
        due_date: values.due_date?.toISOString().split('T')[0] || null,
        reminder_date: values.reminder_date?.toISOString() || null,
        // Convert empty strings/undefined to null for database
        lead_id: values.lead_id || null,
        contact_id: values.contact_id || null,
        assigned_to: values.assigned_to || null,
      };

      const { error } = await supabase.functions.invoke("tasks-operations", {
        body: {
          action: taskId ? "update" : "create",
          task: taskData,
          taskId: taskId,
          userId: session.user.id,
          organizationId: currentOrganization.id
        },
      });

      if (error) throw error;

      // Invalidate queries to refresh the data
      await queryClient.invalidateQueries({ queryKey: ["tasks"] });
      if (leadId) {
        await queryClient.invalidateQueries({ queryKey: ["tasks", leadId] });
      }

      toast({
        title: "Success",
        description: `Task ${taskId ? "updated" : "created"} successfully`,
      });

      form.reset();
      onSuccess?.();
    } catch (error: any) {
      console.error("Task submission error:", error);
      toast({
        title: "Error",
        description: error.message || `Failed to ${taskId ? "update" : "create"} task`,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!currentOrganization) {
    return <div>Please select an organization to manage tasks.</div>;
  }

  const isEditing = !!taskId;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <BasicInfoFields form={form} />
        <StatusFields form={form} />
        <DateFields form={form} />
        <AssociationFields form={form} />
        
        <div className="flex justify-between gap-4">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Saving..." : isEditing ? "Update Task" : "Create Task"}
          </Button>
        </div>
      </form>
    </Form>
  );
};
